import { Button } from "@/components/ui/button";
import { Logo } from "./Logo";
import { Link, useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Zap, Calendar, User, Phone } from "lucide-react";
import { useState, useCallback, startTransition } from "react";
import { HighContrastToggle } from "./HighContrastToggle";
import { FontSizeToggle } from "./FontSizeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { REGIONS, useLocation, ALL_LA_SUB_REGIONS } from "@/lib/locations";
import { SeasonalBanner } from "./SeasonalBanner";

const BOOKING_URL = "/book";
const LOGIN_URL = "/login";

// Map English region/city slugs -> Spanish page paths
export const SLUG_TO_ES: Record<string, string> = {
  "sacramento-area": "/es/sacramento",
  "sacramento": "/es/sacramento",
  "roseville": "/es/roseville",
  "folsom": "/es/folsom",
  "elk-grove": "/es/elk-grove",
  "davis": "/es/davis",
  "rocklin": "/es/rocklin",
  "granite-bay": "/es/granite-bay",
  "el-dorado-hills": "/es/el-dorado-hills",
  "west-sacramento": "/es/west-sacramento",
  "rancho-cordova": "/es/rancho-cordova",
  "carmichael": "/es/carmichael",
  "citrus-heights": "/es/citrus-heights",
  "los-angeles": "/es/los-angeles",
  "santa-monica": "/es/santa-monica",
  "venice": "/es/venice",
  "brentwood-los-angeles": "/es/brentwood-los-angeles",
  "west-la": "/es/west-la",
  "culver-city": "/es/culver-city",
  "sherman-oaks": "/es/sherman-oaks",
  "encino": "/es/encino",
  "studio-city": "/es/studio-city",
  "woodland-hills": "/es/woodland-hills",
  "porter-ranch": "/es/porter-ranch",
  "pasadena": "/es/pasadena",
  "arcadia": "/es/arcadia",
  "san-marino": "/es/san-marino",
  "monrovia": "/es/monrovia",
  "manhattan-beach": "/es/manhattan-beach",
  "redondo-beach": "/es/redondo-beach",
  "torrance": "/es/torrance",
  "el-segundo": "/es/el-segundo",
  "contra-costa": "/es/contra-costa",
  "walnut-creek": "/es/walnut-creek",
  "concord": "/es/concord",
  "antioch": "/es/antioch",
  "richmond": "/es/richmond",
  "solano": "/es/solano",
  "vallejo": "/es/vallejo",
  "fairfield": "/es/fairfield",
  "vacaville": "/es/vacaville",
  "benicia": "/es/benicia",
  "alameda": "/es/alameda",
  "oakland": "/es/oakland",
  "fremont": "/es/fremont",
  "hayward": "/es/hayward",
  "berkeley": "/es/berkeley",
  "pleasanton": "/es/pleasanton",
  "dublin": "/es/dublin",
  "livermore": "/es/livermore",
  "tri-valley": "/es/tri-valley",
  "san-ramon": "/es/san-ramon",
  "san-francisco": "/es/san-francisco",
  "san-jose": "/es/san-jose",
  "palo-alto": "/es/palo-alto",
  "mountain-view": "/es/mountain-view",
  "sunnyvale": "/es/sunnyvale",
  "santa-clara": "/es/santa-clara",
  "san-diego": "/es/san-diego",
  "irvine": "/es/irvine",
  "orange-county": "/es/orange-county",
  "huntington-beach": "/es/huntington-beach",
  "newport-beach": "/es/newport-beach",
  "anaheim": "/es/anaheim",
  "santa-ana": "/es/santa-ana",
  "marin": "/es/marin",
  "san-rafael": "/es/san-rafael",
  "phoenix-area": "/es/phoenix",
  "phoenix": "/es/phoenix",
  "scottsdale": "/es/scottsdale",
  "tempe": "/es/tempe",
  "mesa": "/es/mesa",
  "chandler": "/es/chandler",
  "gilbert": "/es/gilbert",
  "glendale": "/es/glendale",
  "peoria": "/es/peoria",
  "surprise": "/es/surprise",
  "goodyear": "/es/goodyear",
  "pricing": "/es/precios",
  "faq": "/es/preguntas-frecuentes",
  "locations": "/es/locations",
  "blog": "/es/blog",
  "services/move-in-out": "/es/limpieza-mudanza",
  "services/deep-cleaning": "/es/limpieza-profunda",
};

type LangCode = "en" | "es" | "ru" | "zh";

function detectLang(pathname: string): LangCode {
  if (pathname.startsWith("/es/")) return "es";
  if (pathname.startsWith("/ru/")) return "ru";
  if (pathname.startsWith("/zh/")) return "zh";
  return "en";
}

function getLocalizedPath(slug: string, lang: LangCode): string {
  const s = slug === "sacramento-area" ? "sacramento" : slug;
  if (lang === "es") return SLUG_TO_ES[slug] || "/es/sacramento";
  if (lang === "ru") return `/ru/${s}`;
  if (lang === "zh") return `/zh/${s}`;
  return "/";
}

function stripLangPrefix(pathname: string): string {
  const stripped = pathname.replace(/^\/(es|ru|zh)\//, "/locations/");
  if (stripped === "/locations/sacramento") return "/";
  return stripped;
}

// 4-way Language selector dropdown
const LangToggle = ({ compact = false }: { compact?: boolean }) => {
  const routerLocation = useRouterLocation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const currentLang = detectLang(routerLocation.pathname);

  const LANGS: { code: LangCode; label: string; flag: string }[] = [
    { code: "en", label: "English", flag: "🇺🇸" },
    { code: "es", label: "Español", flag: "🇲🇽" },
    { code: "ru", label: "Русский", flag: "🇷🇺" },
    { code: "zh", label: "中文", flag: "🇨🇳" },
  ];

  const current = LANGS.find(l => l.code === currentLang) || LANGS[0];

  const handleSelect = useCallback((lang: LangCode) => {
    setOpen(false);
    localStorage.setItem("langPref", lang);
    if (lang === "en") {
      startTransition(() => { navigate(stripLangPrefix(routerLocation.pathname)); });
    } else {
      startTransition(() => { navigate(getLocalizedPath(location.slug, lang)); });
    }
  }, [routerLocation.pathname, location.slug, navigate]);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        onTouchEnd={(e) => { e.preventDefault(); setOpen(o => !o); }}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ WebkitTapHighlightColor: "transparent", touchAction: "manipulation" }}
        className={`flex items-center gap-1.5 rounded-full border-2 border-border bg-muted hover:border-primary/50 hover:shadow-sm active:scale-95 transition-all duration-200 cursor-pointer select-none px-3 ${compact ? "h-8 text-[10px]" : "h-9 text-xs"} font-extrabold`}
      >
        <span>{current.flag}</span>
        <span className="text-foreground">{current.code.toUpperCase()}</span>
        <span className="text-muted-foreground text-[8px] leading-none">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-2 right-0 bg-background border border-border rounded-xl shadow-xl z-50 overflow-hidden min-w-[150px]">
            {LANGS.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className={`flex items-center gap-2.5 w-full px-4 py-2.5 text-sm font-medium hover:bg-muted transition-colors ${lang.code === currentLang ? "text-primary bg-primary/5" : "text-foreground"}`}
              >
                <span>{lang.flag}</span>
                <span>{lang.label}</span>
                {lang.code === currentLang && <span className="ml-auto text-primary text-xs">✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [touchStartX, setTouchStartX] = useState(0);
  const location = useLocation();
  const routerLocation = useRouterLocation();
  const currentLang = detectLang(routerLocation.pathname);
  const isSpanish = currentLang === "es";
  const isRussian = currentLang === "ru";
  const isChinese = currentLang === "zh";
  const currentRegion = REGIONS[location.regionSlug];

  const t = (en: string, es: string, ru: string, zh: string) =>
    isSpanish ? es : isRussian ? ru : isChinese ? zh : en;

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetPath: string, isMobile?: boolean) => {
    if (routerLocation.pathname === targetPath) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    if (isMobile) {
      setIsOpen(false);
    }
  };

  const navLinkClass = (path: string) => {
    const isActive = routerLocation.pathname === path;
    return `text-sm transition-colors ${isActive ? "text-primary font-bold underline underline-offset-4" : "font-medium hover:text-primary"}`;
  };

  const pricingPath = isSpanish ? "/es/precios" : isRussian ? "/ru/pricing" : isChinese ? "/zh/pricing" : "/pricing";
  const faqPath = isSpanish ? '/es/preguntas-frecuentes' : isRussian ? '/ru/faq' : isChinese ? '/zh/faq' : (location.slug === 'sacramento-area' ? '/faq' : `/locations/${location.slug}/faq`);

  return (
    <>
      <SeasonalBanner />
      <div className="sticky top-0 z-50 w-full flex flex-col shadow-md bg-background">
      <nav className="w-full border-b bg-background">
        <div className="container flex h-20 items-center justify-between">
          <div className="flex items-center gap-6 shrink-0 min-w-0">
            <Link to="/" onClick={(e) => handleNavClick(e, "/")} className="flex items-center shrink-0 min-w-0 overflow-hidden">
              <Logo className="truncate" />
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-4">
            <Link to="/" onClick={(e) => handleNavClick(e, "/")} className={navLinkClass("/")}>{t("Home","Inicio","Главная","首页")}</Link>
            <Link to="/services" onClick={(e) => handleNavClick(e, "/services")} className={navLinkClass("/services")}>{t("Services","Servicios","Услуги","服务")}</Link>
            <Link to={pricingPath} onClick={(e) => handleNavClick(e, pricingPath)} className={navLinkClass(pricingPath)}>{t("Pricing","Precios","Цены","价格")}</Link>

            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary outline-none">
                {t("Service Areas","Áreas de Servicio","Зоны обслуживания","服务区域")} <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 p-2 max-h-80 overflow-y-auto">
                <DropdownMenuItem asChild>
                  <Link to="/locations" className="font-bold text-primary">{t("All Service Areas","Todas las Áreas","Все зоны","所有服务区域")}</Link>
                </DropdownMenuItem>
                {isSpanish && (
                  <DropdownMenuItem asChild>
                    <Link to="/es/locations" className="font-bold text-primary">🇲🇽 Áreas en Español</Link>
                  </DropdownMenuItem>
                )}
                <hr className="my-1" />
                {location.regionSlug === 'los-angeles' ? (
                  <>
                    <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider px-2 py-1">{t("LA Sub-Regions","Subregiones de LA","Подрайоны LA","洛杉矶子区")}</DropdownMenuLabel>
                    {ALL_LA_SUB_REGIONS.map((sub) => (
                      <DropdownMenuItem key={sub.slug} asChild>
                        <Link to={`/locations/la/${sub.slug}`} className="font-medium">{sub.shortName}</Link>
                      </DropdownMenuItem>
                    ))}
                    <hr className="my-1" />
                    <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider px-2 py-1">{t("All LA Service Areas","Todas las Áreas de LA","Все зоны LA","洛杉矶所有服务区域")}</DropdownMenuLabel>
                    {currentRegion.cities.map((city) => (
                      <DropdownMenuItem key={city} asChild>
                        <Link to={`/locations/${city.toLowerCase().replace(/\s+/g, '-')}`} className="text-sm">{city}</Link>
                      </DropdownMenuItem>
                    ))}
                  </>
                ) : (
                  <>
                    <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider px-2 py-1">California</DropdownMenuLabel>
                    {currentRegion.cities.map((city) => (
                      <DropdownMenuItem key={city} asChild>
                        <Link to={`/locations/${city.toLowerCase().replace(/\s+/g, '-')}`}>{city}</Link>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/offers" onClick={(e) => handleNavClick(e, "/offers")} className={navLinkClass("/offers")}>Offers</Link>
            <Link to={faqPath} onClick={(e) => handleNavClick(e, faqPath)} className={navLinkClass(faqPath)}>{t("FAQ","Preguntas","Вопросы","常见问题")}</Link>

            {/* Language Toggle */}
            <LangToggle />
            <div className="flex items-center gap-1">
              <HighContrastToggle />
              <FontSizeToggle />
            </div>

            <div className="flex items-center gap-1 xl:gap-2 border-l pl-2 xl:pl-3">
              <Button asChild variant="ghost" className="font-semibold text-muted-foreground hover:text-primary px-2">
                <a href="tel:9162995588"><Phone className="h-4 w-4 xl:mr-1.5" /> <span className="hidden xl:inline">{t("Call Now","Llamar","Позвонить","立即致电")}</span><span className="hidden lg:inline xl:hidden">Call</span></a>
              </Button>

              <Button asChild variant="outline" className="font-semibold border-primary text-primary hover:bg-primary/10 hover:text-primary px-3 xl:px-4">
                <a href={`${BOOKING_URL}?view=custom-plan`}><Zap className="h-4 w-4 xl:mr-1.5 fill-primary/20" /> <span className="hidden xl:inline">{t("Instant Price","Precio al Instante","Цена сейчас","即时报价")}</span><span className="hidden lg:inline xl:hidden">Quote</span></a>
              </Button>
              <Button asChild className="font-semibold bg-primary text-primary-foreground hover:bg-primary-hover shadow-md shadow-primary/20 px-3 xl:px-4">
                <a href={`${BOOKING_URL}?view=custom-plan`}><Calendar className="h-4 w-4 xl:mr-1.5" /> <span className="hidden xl:inline">{t("Book Now","Reservar","Записаться","立即预约")}</span><span className="hidden lg:inline xl:hidden">Book</span></a>
              </Button>
            </div>
          </div>

          {/* Mobile Nav Actions */}
          <div className="flex items-center gap-1.5 sm:gap-2 lg:hidden">
            <a href="tel:9162995588" className="p-1.5 text-muted-foreground hover:text-primary transition-colors" aria-label="Call Now">
              <Phone className="h-5 w-5" />
            </a>
            <Button asChild size="sm" className="font-semibold bg-primary text-primary-foreground hover:bg-primary-hover px-3 sm:px-4">
              <a href={BOOKING_URL}>
                <Calendar className="h-4 w-4 sm:mr-1.5" /> 
                <span className="hidden sm:inline">{t("Book Now","Reservar","Записаться","预约")}</span>
                <span className="sm:hidden">{t("Book","Reservar","Бронь","预约")}</span>
              </a>
            </Button>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <button className="p-1.5 sm:p-2 text-muted-foreground" aria-label={isOpen ? "Close menu" : "Open menu"}>
                  <Menu className="h-6 w-6" aria-hidden="true" />
                </button>
              </SheetTrigger>
              <SheetContent 
                side="right" 
                className="w-[300px] sm:w-[350px] overflow-y-auto pt-10"
                onTouchStart={(e) => setTouchStartX(e.touches[0].clientX)}
                onTouchEnd={(e) => {
                  if (e.changedTouches[0].clientX - touchStartX > 50) {
                    setIsOpen(false);
                  }
                }}
              >
                <SheetHeader className="text-left mb-0">
                  <SheetTitle className="sr-only">Mobile Menu</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between py-2 animate-in slide-in-from-right fade-in duration-300 fill-mode-both delay-75">
                    <span className="text-lg font-medium">{t("Language","Idioma","Язык","语言")}</span>
                    <div className="flex items-center gap-2">
                      <FontSizeToggle />
                      <HighContrastToggle />
                      <LangToggle />
                    </div>
                  </div>
                  <Link to="/" className={`text-lg py-2 animate-in slide-in-from-right fade-in duration-300 fill-mode-both delay-75 ${routerLocation.pathname === "/" ? "text-primary font-bold" : "font-medium"}`} onClick={(e) => handleNavClick(e, "/", true)}>{t("Home","Inicio","Главная","首页")}</Link>
                  <Link to="/services" className={`text-lg py-2 animate-in slide-in-from-right fade-in duration-300 fill-mode-both delay-100 ${routerLocation.pathname === "/services" ? "text-primary font-bold" : "font-medium"}`} onClick={(e) => handleNavClick(e, "/services", true)}>{t("Services","Servicios","Услуги","服务")}</Link>
                  <Link to={pricingPath} className={`text-lg py-2 animate-in slide-in-from-right fade-in duration-300 fill-mode-both delay-150 ${routerLocation.pathname === pricingPath ? "text-primary font-bold" : "font-medium"}`} onClick={(e) => handleNavClick(e, pricingPath, true)}>{t("Pricing","Precios","Цены","价格")}</Link>
                  <Link to="/locations" className={`text-lg py-2 animate-in slide-in-from-right fade-in duration-300 fill-mode-both delay-200 ${routerLocation.pathname === "/locations" ? "text-primary font-bold" : "font-medium"}`} onClick={(e) => handleNavClick(e, "/locations", true)}>{t("All Service Areas","Todas las Áreas","Все зоны","所有服务区域")}</Link>
                  {isSpanish && (
                    <Link to="/es/locations" className="text-lg font-medium py-2 text-primary animate-in slide-in-from-right fade-in duration-300 fill-mode-both delay-200" onClick={() => setIsOpen(false)}>🇲🇽 Áreas en Español</Link>
                  )}
                  {isRussian && (
                    <Link to="/ru/sacramento" className="text-lg font-medium py-2 text-primary animate-in slide-in-from-right fade-in duration-300 fill-mode-both delay-200" onClick={() => setIsOpen(false)}>🇷🇺 Страницы на русском</Link>
                  )}
                  {isChinese && (
                    <Link to="/zh/sacramento" className="text-lg font-medium py-2 text-primary animate-in slide-in-from-right fade-in duration-300 fill-mode-both delay-200" onClick={() => setIsOpen(false)}>🇨🇳 中文页面</Link>
                  )}
                  {location.regionSlug === 'los-angeles' && (
                    <div className="pl-4 flex flex-col gap-2 animate-in slide-in-from-right fade-in duration-300 fill-mode-both delay-200">
                      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("LA Sub-Regions","Subregiones de LA","Подрайоны LA","洛杉矶子区")}</p>
                      {ALL_LA_SUB_REGIONS.map(sub => (
                        <Link key={sub.slug} to={`/locations/la/${sub.slug}`} className="text-base font-medium py-1 text-muted-foreground hover:text-primary transition-colors" onClick={() => setIsOpen(false)}>
                          {sub.shortName}
                        </Link>
                      ))}
                    </div>
                  )}
                  <Link to="/offers" className={`text-lg py-2 animate-in slide-in-from-right fade-in duration-300 fill-mode-both delay-300 ${routerLocation.pathname === "/offers" ? "text-primary font-bold" : "font-medium"}`} onClick={(e) => handleNavClick(e, "/offers", true)}>Offers</Link>
                  <Link to={faqPath} className={`text-lg py-2 animate-in slide-in-from-right fade-in duration-300 fill-mode-both delay-300 ${routerLocation.pathname === faqPath ? "text-primary font-bold" : "font-medium"}`} onClick={(e) => handleNavClick(e, faqPath, true)}>{t("FAQ","Preguntas","Вопросы","常见问题")}</Link>
                  <hr className="animate-in slide-in-from-right fade-in duration-300 fill-mode-both delay-300" />
                  <div className="flex flex-col gap-3 animate-in slide-in-from-right fade-in duration-300 fill-mode-both delay-500">
                    <Button asChild variant="ghost" className="w-full font-semibold text-muted-foreground justify-start">
                      <a href="tel:9162995588"><Phone className="h-4 w-4 mr-2" /> {t("Call Now","Llamar","Позвонить","立即致电")}</a>
                    </Button>

                    <Button asChild variant="outline" className="w-full font-semibold border-primary text-primary hover:bg-primary/10 hover:text-primary justify-start">
                      <a href={`${BOOKING_URL}?view=custom-plan`}><Zap className="h-4 w-4 mr-2 fill-primary/20" /> {t("Instant Price","Precio al Instante","Цена сейчас","即时报价")}</a>
                    </Button>
                    <Button asChild className="w-full font-semibold bg-primary text-primary-foreground justify-start">
                      <a href={`${BOOKING_URL}?view=custom-plan`}><Calendar className="h-4 w-4 mr-2" /> {t("Book Now","Reservar","Записаться","立即预约")}</a>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* Mobile Sticky Sub-navigation */}
      <div className="lg:hidden w-full border-b bg-background overflow-x-auto hide-scrollbar">
        <div className="container flex items-center gap-6 h-10 min-w-max px-4">
          <Link to="/" onClick={(e) => handleNavClick(e, "/")} className={`text-xs transition-colors ${routerLocation.pathname === "/" ? "text-primary font-bold underline underline-offset-4" : "font-semibold text-muted-foreground hover:text-primary"}`}>{t("Home","Inicio","Главная","首页")}</Link>
          <Link to="/services" onClick={(e) => handleNavClick(e, "/services")} className={`text-xs transition-colors ${routerLocation.pathname === "/services" ? "text-primary font-bold underline underline-offset-4" : "font-semibold text-muted-foreground hover:text-primary"}`}>{t("Services","Servicios","Услуги","服务")}</Link>
          <Link to={pricingPath} onClick={(e) => handleNavClick(e, pricingPath)} className={`text-xs transition-colors ${routerLocation.pathname === pricingPath ? "text-primary font-bold underline underline-offset-4" : "font-semibold text-muted-foreground hover:text-primary"}`}>{t("Pricing","Precios","Цены","价格")}</Link>
          <Link to="/offers" onClick={(e) => handleNavClick(e, "/offers")} className={`text-xs transition-colors ${routerLocation.pathname === "/offers" ? "text-primary font-bold underline underline-offset-4" : "font-semibold text-muted-foreground hover:text-primary"}`}>Offers</Link>
          <Link to={faqPath} onClick={(e) => handleNavClick(e, faqPath)} className={`text-xs transition-colors ${routerLocation.pathname === faqPath ? "text-primary font-bold underline underline-offset-4" : "font-semibold text-muted-foreground hover:text-primary"}`}>{t("FAQ","Preguntas","Вопросы","常见问题")}</Link>
        </div>
      </div>
      </div>
    </>
  );
};
